import React, { useState, useEffect, useCallback } from 'react';
import { isEmpty } from 'lodash';

/**
 * Custom hook for fetching and managing nearby facilities.
 * @param {Object} options
 * @param {Object} options.facility - The current facility
 * @param {Array} options.nearbyFacilities - Existing nearby facilities from props
 * @param {Object} options.actions - Actions object with fetchSearchResults
 * @param {number} [options.listingsPerPage=5] - Number of listings per page
 * @param {string} [options.searchType='storage-only'] - Search type
 */
export const useNearbyFacilities = ({
	facility,
	nearbyFacilities: existingNearbyFacilities,
	actions,
	listingsPerPage = 5,
	searchType = 'storage-only',
}) => {
	const [facilityIds, setFacilityIds] = useState([]);
	const [loadingNearbyFacilities, setLoadingNearbyFacilities] =
		useState(true);
	const [nearbySearchId, setNearbySearchId] = useState(null);

	const handleSearchResponse = useCallback(({ error, payload }) => {
		if (!error) {
			const search = payload.entities.search[payload.result.search];
			setFacilityIds(search.facilities);
			setLoadingNearbyFacilities(false);
			setNearbySearchId(search.searchId);
		} else {
			setLoadingNearbyFacilities(false);
		}
	}, []);

	useEffect(() => {
		if (isEmpty(existingNearbyFacilities)) {
			const { latitude, longitude } = facility.location;
			actions
				.fetchSearchResults({
					latitude,
					longitude,
					listingsPerPage,
					searchType,
				})
				.then(handleSearchResponse);
		}
	}, []); // eslint-disable-line

	return {
		facilityIds,
		loadingNearbyFacilities,
		nearbySearchId,
	};
};

function withNearbyFacilities(WrappedComponent) {
	const WithNearbyFacilities = (props) => {
		const hookResult = useNearbyFacilities(props);
		return (
			<WrappedComponent
				{...props}
				{...hookResult}
			/>
		);
	};
	WithNearbyFacilities.displayName = `withNearbyFacilities(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
	return WithNearbyFacilities;
}

export default withNearbyFacilities;
